import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type MouseEvent as ReactMouseEvent } from "react";
import styles from "./SwipeRow.module.css";
import { TrashIcon } from "./TrashIcon";

const ACTION_WIDTH = 72;
const THRESHOLD = 10;
const RUBBER = 0.55;

/** The further past the bound, the less the element follows. */
function rubberband(overshoot: number, dimension: number): number {
  return (overshoot * dimension * RUBBER) / (dimension + RUBBER * Math.abs(overshoot));
}

/** Where a flick would come to rest (Apple's exponential decay), in px. */
function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

interface Gesture {
  startX: number;
  startY: number;
  startOffset: number;
  decided: boolean;
  horizontal: boolean;
  moved: boolean;
  history: { x: number; t: number }[];
}

interface Props {
  onDelete: () => void;
  deleteLabel: string;
  children: ReactNode;
}

/**
 * Swipe the content left to reveal a red delete action; tracks the pointer 1:1,
 * rubber-bands past the edges, projects the release velocity to decide
 * open/closed and starts every new drag from the live on-screen position.
 */
export function SwipeRow({ onDelete, deleteLabel, children }: Props) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const content = useRef<HTMLDivElement>(null);
  const open = offset <= -ACTION_WIDTH / 2;

  const liveOffset = (): number => {
    const element = content.current;
    if (!element || typeof DOMMatrix === "undefined") return offset;
    const transform = getComputedStyle(element).transform;
    if (!transform || transform === "none") return offset;
    try {
      return new DOMMatrix(transform).m41;
    } catch {
      return offset;
    }
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    gesture.current = {
      startX: e.clientX, startY: e.clientY, startOffset: liveOffset(),
      decided: false, horizontal: false, moved: false, history: [{ x: e.clientX, t: performance.now() }],
    };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.decided) {
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
      g.decided = true;
      g.horizontal = Math.abs(dx) > Math.abs(dy);
      if (g.horizontal) {
        setDragging(true);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* jsdom and old browsers: tracking still works while the pointer stays inside */
        }
      }
    }
    if (!g.horizontal) return;
    g.moved = true;
    g.history = [...g.history.slice(-4), { x: e.clientX, t: performance.now() }];
    let next = g.startOffset + dx;
    if (next > 0) next = rubberband(next, ACTION_WIDTH);
    else if (next < -ACTION_WIDTH) next = -ACTION_WIDTH + rubberband(next + ACTION_WIDTH, ACTION_WIDTH);
    setOffset(next);
  };

  const finish = () => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || !g.horizontal) return;
    setDragging(false);
    const first = g.history[0];
    const last = g.history[g.history.length - 1];
    const seconds = first && last ? (last.t - first.t) / 1000 : 0;
    const velocity = first && last && seconds > 0 ? (last.x - first.x) / seconds : 0;
    const projected = offset + project(velocity);
    setOffset(projected < -ACTION_WIDTH / 2 ? -ACTION_WIDTH : 0);
  };

  /** A tap on an open row closes it instead of acting on the content. */
  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (open || gesture.current?.moved) {
      e.preventDefault();
      e.stopPropagation();
      setOffset(0);
    }
  };

  return (
    <div className={styles.row}>
      <button
        type="button"
        className={styles.action}
        aria-label={deleteLabel}
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
        /* Hidden while the row rests so its red edge cannot bleed through the rounded corners above it. */
        style={{ visibility: offset === 0 ? "hidden" : undefined }}
        onClick={() => {
          setOffset(0);
          onDelete();
        }}
      >
        <TrashIcon />
      </button>
      <div
        ref={content}
        className={styles.content}
        data-dragging={dragging}
        style={{ transform: `translateX(${offset}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>
    </div>
  );
}
